import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FaTimes, FaFileAlt, FaMapMarkerAlt, FaShip, FaBox, FaUser, FaCalendarAlt } from 'react-icons/fa'
import { clearanceService } from '../services/clearanceService'
import { exportToCSV } from '../utils/csvExport'

const ClearancePage = () => {
  const navigate = useNavigate()
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [selectedRequestLoading, setSelectedRequestLoading] = useState(false)

  const fetchClearanceRequests = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const filters = {
        limit: 100,
        offset: 0,
      }
      if (statusFilter !== 'all') {
        filters.status = statusFilter
      }
      if (typeFilter !== 'all') {
        filters.requestType = typeFilter
      }

      const result = await clearanceService.getAllClearanceRequests(filters)

      if (result.success) {
        setRequests(result.data || [])
      } else {
        setError(result.error)
        toast.error(result.message || 'Failed to fetch clearance requests')
      }
    } catch (error) {
      setError(error.message)
      toast.error('An error occurred while fetching clearance requests')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, typeFilter])

  // Fetch clearance requests when filters change
  useEffect(() => {
    fetchClearanceRequests()
  }, [fetchClearanceRequests])

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchClearanceRequests()
    }, 60000)

    return () => clearInterval(interval)
  }, [fetchClearanceRequests])

  const getRequestTypeLabel = (type) => {
    switch (type) {
      case 'freight_forwarding':
        return 'Freight Forwarding'
      case 'import':
        return 'Import Clearance'
      case 'export':
        return 'Export Clearance'
      default:
        return type || 'Clearance Request'
    }
  }

  const getStatusBadgeClass = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'pending':      return 'bg-[#fef5e7] text-[#e67e22]'
      case 'under_review': return 'bg-[#eaf4fb] text-[#3498db]'
      case 'approved':     return 'bg-[#eafaf1] text-[#27ae60]'
      case 'rejected':     return 'bg-[#fdedec] text-[#e74c3c]'
      default:             return 'bg-[#f8f9fa] text-[#7f8c8d]'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '—'
    try {
      const date = new Date(dateString)
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return String(dateString)
    }
  }

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      const result = await clearanceService.updateClearanceStatus(requestId, newStatus)
      if (result.success) {
        toast.success('Clearance request status updated successfully')
        fetchClearanceRequests()
      } else {
        toast.error(result.message || 'Failed to update status')
      }
    } catch (error) {
      toast.error('An error occurred while updating status')
    }
  }

  const handleViewDetails = async (req) => {
    setSelectedRequestLoading(true)
    try {
      const result = await clearanceService.getClearanceRequestById(req.id)
      if (result.success && result.data) {
        setSelectedRequest(result.data)
      } else {
        toast.error(result.message || 'Failed to load clearance request details')
      }
    } catch (error) {
      toast.error('An error occurred while loading clearance request details')
    } finally {
      setSelectedRequestLoading(false)
    }
  }

  const handleDelete = async (requestId) => {
    if (window.confirm('Are you sure you want to delete this clearance request?')) {
      try {
        const result = await clearanceService.deleteClearanceRequest(requestId)
        if (result.success) {
          toast.success('Clearance request deleted successfully')
          fetchClearanceRequests()
        } else {
          toast.error(result.message || 'Failed to delete clearance request')
        }
      } catch (error) {
        toast.error('An error occurred while deleting clearance request')
      }
    }
  }

  const handleExportCSV = () => {
    const headers = [
      { label: 'Request ID', key: 'id' },
      { label: 'Type', key: 'requestType' },
      { label: 'Status', key: 'status' },
      { label: 'City', key: 'city' },
      { label: 'Container Type', key: 'containerType' },
      { label: 'Transport Mode', key: 'transportMode' },
      { label: 'Created By', key: 'Creator.name' },
      { label: 'Created At', key: 'createdAt' },
    ]

    const filename = `clearance_requests_${new Date().toISOString().split('T')[0]}.csv`
    exportToCSV(requests, headers, filename)
  }

  const DetailField = ({ label, value }) => (
    <div>
      <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm text-[#2c3e50]">{value || '—'}</p>
    </div>
  )

  const SectionCard = ({ icon: Icon, title, children }) => (
    <div className="bg-white border border-[#e9ecef] rounded-lg p-5">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#e9ecef]">
        <Icon className="text-[#3498db]" />
        <h3 className="text-sm font-semibold text-[#2c3e50]">{title}</h3>
      </div>
      {children}
    </div>
  )

  return (
    <div className="px-[20px] md:px-[40px] py-[30px] min-h-screen bg-[#f8f9fa]">
      {/* Page Header */}
      <div className="flex items-center justify-between pb-5 mb-6 border-b border-[#e9ecef]">
        <h1 className="text-[1.8rem] font-semibold text-[#2c3e50]">Clearance Requests</h1>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-[#e9ecef] rounded-lg px-4 py-2 text-sm text-[#2c3e50] focus:outline-none focus:border-[#3498db]"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-[#e9ecef] rounded-lg px-4 py-2 text-sm text-[#2c3e50] focus:outline-none focus:border-[#3498db]"
          >
            <option value="all">All Types</option>
            <option value="import">Import</option>
            <option value="export">Export</option>
            <option value="freight_forwarding">Freight Forwarding</option>
          </select>
          <button
            onClick={handleExportCSV}
            className="bg-[#3498db] hover:bg-[#2980b9] text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-4 border-[#3498db] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-[#fdedec] border border-[#e74c3c]/30 text-[#e74c3c] px-4 py-3 rounded-lg mb-5 text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm overflow-x-auto">
          {requests.length === 0 ? (
            <div className="text-center py-16">
              <FaFileAlt className="text-[#bdc3c7] text-4xl mx-auto mb-3" />
              <p className="text-[#7f8c8d]">No clearance requests match the current filters</p>
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-[#e9ecef] bg-[#f8f9fa]">
                  {['Request ID', 'Type', 'Status', 'City', 'Container', 'Transport', 'Created By', 'Date', 'Docs', 'Actions'].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-medium text-[#7f8c8d] uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <tr key={req.id} className="border-b border-[#e9ecef] hover:bg-[#f8f9fa] transition-colors">
                    <td className="px-5 py-3 font-mono text-xs text-[#2c3e50]">C360-PK-{req.id}</td>
                    <td className="px-5 py-3 text-[#2c3e50]">{getRequestTypeLabel(req.requestType)}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadgeClass(req.status)}`}>
                        {(req.status || 'pending').replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[#7f8c8d]">{req.city || '—'}</td>
                    <td className="px-5 py-3 text-[#7f8c8d]">{req.containerType || '—'}</td>
                    <td className="px-5 py-3 text-[#7f8c8d]">{req.transportMode || '—'}</td>
                    <td className="px-5 py-3 text-[#2c3e50]">{req.Creator?.name || 'Unknown'}</td>
                    <td className="px-5 py-3 text-[#7f8c8d] text-xs">{formatDate(req.createdAt)}</td>
                    <td className="px-5 py-3 text-[#7f8c8d]">{req.Documents?.length || 0}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          onClick={() => handleViewDetails(req)}
                          className="px-2.5 py-1 bg-[#f0e6ff] text-[#8e44ad] hover:bg-[#8e44ad] hover:text-white rounded text-xs font-medium transition-colors"
                        >
                          Details
                        </button>
                        {req.status === 'pending' && (
                          <button
                            onClick={() => handleUpdateStatus(req.id, 'under_review')}
                            className="px-2.5 py-1 bg-[#eaf4fb] text-[#3498db] hover:bg-[#3498db] hover:text-white rounded text-xs font-medium transition-colors"
                          >
                            Review
                          </button>
                        )}
                        {req.status === 'under_review' && (
                          <>
                            <button
                              onClick={() => handleUpdateStatus(req.id, 'approved')}
                              className="px-2.5 py-1 bg-[#eafaf1] text-[#27ae60] hover:bg-[#27ae60] hover:text-white rounded text-xs font-medium transition-colors"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(req.id, 'rejected')}
                              className="px-2.5 py-1 bg-[#fdedec] text-[#e74c3c] hover:bg-[#e74c3c] hover:text-white rounded text-xs font-medium transition-colors"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(req.id)}
                          className="px-2.5 py-1 bg-[#fdedec] text-[#e74c3c] hover:bg-[#e74c3c] hover:text-white rounded text-xs font-medium transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Details Modal */}
      {(selectedRequestLoading || selectedRequest) && (
        <div
          className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4"
          onClick={() => { if (!selectedRequestLoading) setSelectedRequest(null) }}
        >
          <div
            className="bg-white rounded-xl w-full max-w-[920px] max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-[#e9ecef] px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h2 className="text-lg font-semibold text-[#2c3e50]">
                {selectedRequestLoading ? 'Loading…' : 'Clearance Request Details'}
              </h2>
              <button
                onClick={() => { if (!selectedRequestLoading) setSelectedRequest(null) }}
                className="text-[#7f8c8d] hover:text-[#2c3e50] transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            {selectedRequestLoading && !selectedRequest && (
              <div className="py-16 flex justify-center">
                <div className="w-7 h-7 border-4 border-[#3498db] border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {selectedRequest && (
              <div className="p-6 space-y-5">
                {/* Basic Info */}
                <div className="bg-[#f8f9fa] border border-[#e9ecef] rounded-lg p-5 grid grid-cols-3 gap-4">
                  <DetailField label="Request ID" value={`C360-PK-${selectedRequest.id}`} />
                  <DetailField label="Type" value={getRequestTypeLabel(selectedRequest.requestType)} />
                  <div>
                    <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1">Status</p>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadgeClass(selectedRequest.status)}`}>
                      {(selectedRequest.status || 'pending').replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Location & Transport */}
                {(selectedRequest.city || selectedRequest.transportMode || selectedRequest.containerType || selectedRequest.port) && (
                  <SectionCard icon={FaMapMarkerAlt} title="Location & Transport">
                    <div className="grid grid-cols-2 gap-4">
                      <DetailField label="City" value={selectedRequest.city} />
                      <DetailField label="Transport Mode" value={selectedRequest.transportMode?.toUpperCase()} />
                      <DetailField label="Container Type" value={selectedRequest.containerType} />
                      <DetailField label="Port" value={selectedRequest.port} />
                    </div>
                  </SectionCard>
                )}

                {/* Shipment Details */}
                {(selectedRequest.pol || selectedRequest.pod || selectedRequest.product || selectedRequest.incoterms || selectedRequest.clearingAgentNum) && (
                  <SectionCard icon={FaShip} title="Shipment Details">
                    <div className="grid grid-cols-2 gap-4">
                      <DetailField label="Port of Loading (POL)" value={selectedRequest.pol} />
                      <DetailField label="Port of Discharge (POD)" value={selectedRequest.pod} />
                      <DetailField label="Product" value={selectedRequest.product} />
                      <DetailField label="Incoterms" value={selectedRequest.incoterms} />
                      <DetailField label="Clearing Agent #" value={selectedRequest.clearingAgentNum} />
                      <DetailField label="CBM" value={selectedRequest.cbm} />
                      <DetailField label="Packages" value={selectedRequest.packages} />
                      <DetailField label="Container Size" value={selectedRequest.containerSize} />
                      <DetailField label="No. of Containers" value={selectedRequest.numberOfContainers} />
                    </div>
                  </SectionCard>
                )}

                {/* Creator */}
                {selectedRequest.Creator && (
                  <SectionCard icon={FaUser} title="Request Creator">
                    <div className="grid grid-cols-2 gap-4">
                      <DetailField label="Name" value={selectedRequest.Creator.name} />
                      <DetailField label="Email" value={selectedRequest.Creator.email} />
                      <DetailField label="Company" value={selectedRequest.Creator.company} />
                    </div>
                  </SectionCard>
                )}

                {/* Documents */}
                {selectedRequest.Documents?.length > 0 && (
                  <SectionCard icon={FaFileAlt} title={`Documents (${selectedRequest.Documents.length})`}>
                    <div className="space-y-2">
                      {selectedRequest.Documents.map((doc, idx) => {
                        const docUrl = doc.signedUrl || doc.fileUrl
                        return (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-[#f8f9fa] border border-[#e9ecef] rounded-lg">
                            <FaFileAlt className="text-[#3498db] flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#2c3e50]">{doc.documentType || 'Document'}</p>
                              {doc.fileName && <p className="text-xs text-[#7f8c8d] truncate">{doc.fileName}</p>}
                            </div>
                            {docUrl && (
                              <a href={docUrl} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-[#3498db] hover:text-[#2980b9] font-medium flex-shrink-0">
                                View →
                              </a>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </SectionCard>
                )}

                {/* Timeline */}
                {(selectedRequest.createdAt || selectedRequest.updatedAt || selectedRequest.Reviewer) && (
                  <SectionCard icon={FaCalendarAlt} title="Timeline & Review">
                    <div className="grid grid-cols-3 gap-4">
                      <DetailField label="Created At" value={formatDate(selectedRequest.createdAt)} />
                      <DetailField label="Last Updated" value={formatDate(selectedRequest.updatedAt)} />
                      <DetailField label="Reviewed By" value={selectedRequest.Reviewer?.name} />
                      {selectedRequest.reviewNotes && (
                        <div className="col-span-3">
                          <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1">Review Notes</p>
                          <p className="text-sm text-[#2c3e50]">{selectedRequest.reviewNotes}</p>
                        </div>
                      )}
                    </div>
                  </SectionCard>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ClearancePage
